import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SettingsSubPageWrapper } from '../Settings';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Store, Plus, Edit, Trash2, MapPin, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BranchesSettings() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', location: '', po_code: '', phone: '', email: '' });

  useEffect(() => { fetchBranches(); }, []);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/branches`, { withCredentials: true });
      setBranches(res.data);
    } catch { toast.error('Failed to load branches'); }
    finally { setLoading(false); }
  };

  const handleSyncFromLoyverse = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/branches/sync-from-loyverse`, {}, { withCredentials: true });
      toast.success(`Synced ${res.data.total_synced} branches (${res.data.new_branches} new, ${res.data.updated_branches} updated)`);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync from Loyverse');
    } finally { setSyncing(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        location: formData.location,
        po_code: formData.po_code,
        phone: formData.phone,
        email: formData.email
      };
      if (editing) {
        await axios.put(`${API}/branches/${editing.branch_id}`, data, { withCredentials: true });
        toast.success('Branch updated');
      } else {
        await axios.post(`${API}/branches`, data, { withCredentials: true });
        toast.success('Branch created');
      }
      setIsModalOpen(false);
      setEditing(null);
      setFormData({ name: '', location: '', po_code: '', phone: '', email: '' });
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save branch');
    }
  };

  const handleEdit = (branch) => {
    setEditing(branch);
    setFormData({
      name: branch.name,
      location: branch.location,
      po_code: branch.po_code || '',
      phone: branch.phone || '',
      email: branch.email || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this branch?')) return;
    try {
      await axios.delete(`${API}/branches/${id}`, { withCredentials: true });
      toast.success('Branch deleted');
      fetchBranches();
    } catch { toast.error('Failed to delete branch'); }
  };

  const openNew = () => {
    setEditing(null);
    setFormData({ name: '', location: '', po_code: '', phone: '', email: '' });
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <SettingsSubPageWrapper title="Branches">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </SettingsSubPageWrapper>
    );
  }

  return (
    <SettingsSubPageWrapper title="Branches">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Branches
          </h1>
          <p className="text-muted-foreground mt-1">Manage your restaurant branches</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSyncFromLoyverse} disabled={syncing} data-testid="sync-branches-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync from Loyverse
          </Button>
          <Button onClick={openNew} data-testid="add-branch-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Branch
          </Button>
        </div>
      </div>

      {branches.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Store className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No branches yet</h3>
              <p className="text-muted-foreground mb-4">Add branches manually or sync from Loyverse</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={handleSyncFromLoyverse} disabled={syncing}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  Sync from Loyverse
                </Button>
                <Button onClick={openNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Manually
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {branches.map((branch) => (
            <Card key={branch.branch_id} className="hover:shadow-md transition-shadow" data-testid={`branch-card-${branch.branch_id}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold">{branch.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mt-0.5">
                      <MapPin className="w-3.5 h-3.5" />
                      {branch.location || 'No location set'}{branch.po_code ? ` · PO code ${branch.po_code}` : ''}{branch.phone ? ` · ${branch.phone}` : ''}{branch.email ? ` · ${branch.email}` : ''}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(branch)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(branch.branch_id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                {branch.loyverse_store_id && (
                  <Badge variant="outline" className="text-xs mb-2">Synced from Loyverse</Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Branch' : 'Add New Branch'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="branch-name">Branch Name *</Label>
              <Input
                id="branch-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Town"
                required
                data-testid="branch-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="branch-location">Address *</Label>
              <Input
                id="branch-location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., 123 Main Street"
                required
                data-testid="branch-location-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="branch-po-code">PO code</Label>
              <Input id="branch-po-code" value={formData.po_code} maxLength={12} placeholder="e.g. Mall"
                onChange={(e) => setFormData({ ...formData, po_code: e.target.value.replace(/[^A-Za-z0-9]/g, '') })}
                data-testid="branch-po-code-input" />
              <p className="text-xs text-muted-foreground">Used in PO numbers, e.g. Mall-2642-001. Letters and numbers only; must be unique. Existing PO numbers don't change.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="branch-phone">Contact number</Label>
                <Input id="branch-phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="044 123 4567" data-testid="branch-phone-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch-email">Email</Label>
                <Input id="branch-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="mall@themerchantcoffee.co.za" data-testid="branch-email-input" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">The location, contact number and email print on purchase orders.</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-branch-btn">{editing ? 'Update' : 'Create'} Branch</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SettingsSubPageWrapper>
  );
}
